import { FC } from 'react';
import { useTranslation } from 'src/hooks/use-translation';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from 'icons/monochrome/close-icon.svg';

import { IS_LOCALHOST } from 'src/config-global';
import { useBillingCheckout } from 'src/context/billing-checkout-context';
import { fMoney } from 'src/utils/format-number';

// ----------------------------------------------------------------------

type Props = {
  variant: 'upcoming' | 'overdue';
  daysOverdue: number;
  daysRemaining: number;
  amountDue: number;
  currency: string;
  paymentUrl: string;
  canPay: boolean;
  onDismiss?: () => void;
  sx?: SxProps<Theme>;
};

export const PaymentBanner: FC<Props> = ({
  variant,
  daysOverdue,
  daysRemaining,
  amountDue,
  currency,
  canPay,
  onDismiss,
  sx,
}) => {
  const { t } = useTranslation('', { keyPrefix: 'payment-banner' });
  const { openCheckout } = useBillingCheckout();

  const days = variant === 'upcoming' ? daysRemaining : daysOverdue;

  const handlePay = () => {
    openCheckout();
    return;
  };

  const payButton = (
    <Button
      variant="outlined"
      size="small"
      color="inherit"
      disabled={!canPay}
      onClick={handlePay}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {t('pay-now')}
    </Button>
  );

  if (IS_LOCALHOST) {
    return null;
  }

  return (
    <Alert
      severity={variant === 'upcoming' ? 'warning' : 'error'}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          {payButton}
          {variant === 'upcoming' && onDismiss && (
            <IconButton size="small" color="inherit" onClick={onDismiss} aria-label={t('dismiss')}>
              <CloseIcon width={20} height={20} />
            </IconButton>
          )}
        </Stack>
      }
      sx={{ borderRadius: 0, ...sx, mt: -2.5, mb: 2 }}
    >
      {t(variant, { days, amount: fMoney(amountDue), currency })}
    </Alert>
  );
};
